import { AgenticLogicRuleWorkspaceSchema } from "@intelligems/ig-types/functions/agentic-logic/workspace/files";
import {
  renderAgenticLogicRequestFieldNotes,
  renderAgenticLogicSdkFieldNotes,
} from "@intelligems/ig-types/dtos/agentic-logic/request-contract";
import {
  AGENTIC_LOGIC_LOGGER_AMBIENT_DTS,
  AGENTIC_LOGIC_SDK_AMBIENT_DTS,
  type AgenticLogicDataSurface,
  buildAgenticLogicTypesModuleSource,
} from "@intelligems/ig-utils/agentic-logic-code-snippets";
import {
  orderedWorkspaceFiles,
  parseWorkspace,
} from "@intelligems/ig-utils/agentic-codegen-workspace";
import type { WorkspaceMonacoConfig } from "@src/features/agenticCodegen/workspace/monaco/workspaceMonacoModels";

import { AGENTIC_LOGIC_TEST_SUITE_COMPANIONS } from "./agenticLogicTestSuiteTypes";

/**
 * The single editable file. Agentic Logic drafts are single-file: the publish
 * manifest is built as `{ [ENTRY]: draft.source }`, so `source` holds one
 * file's contents, NOT a serialized multi-file manifest. The port adapts both
 * directions — read seeds files from `draft.source`, write sends
 * `files[ENTRY]` — and the workspace runs with `allowUserFiles: false`, so the
 * tab bar shows this file plus the locked `types.ts` and offers no
 * add/rename/delete.
 */
export const AGENTIC_LOGIC_ENTRY_FILENAME = "recommendation-rule.ts";
export const AGENTIC_LOGIC_MODEL_URI = `file:///${AGENTIC_LOGIC_ENTRY_FILENAME}`;

/** The readonly contract-types module imported via `./types`. A locked tab. */
export const AGENTIC_LOGIC_TYPES_FILENAME = "types.ts";
export const AGENTIC_LOGIC_TYPES_MODEL_URI = "file:///types.ts";

export const AGENTIC_LOGIC_CONTRACT_MODEL_URI =
  "file:///agentic-logic-contract-check.ts";

/**
 * A ROOT SIBLING of `types.ts`, deliberately not under `node_modules`.
 *
 * `AGENTIC_LOGIC_SDK_AMBIENT_DTS` declares its return types as
 * `import("./types").Foo`, and a relative specifier resolves against the
 * containing file's own directory. Parked at
 * `file:///node_modules/<pkg>/index.d.ts`, `./types` would resolve to
 * `file:///node_modules/<pkg>/types.ts`, which does not exist — every `sdk.*`
 * return type would silently degrade to `any` instead of erroring, so nothing
 * would look broken while completions quietly died. Keeping it at the root
 * makes `./types` resolve to the real `types.ts` companion model.
 */
export const AGENTIC_LOGIC_SDK_TYPES_FILENAME = "sdk.d.ts";
export const AGENTIC_LOGIC_SDK_LIB_URI = `file:///${AGENTIC_LOGIC_SDK_TYPES_FILENAME}`;

/**
 * The logger d.ts has no relative imports — it is a self-contained
 * `declare const logger` — so `node_modules` placement is safe here and this
 * keeps parity with the shipping-provider editor.
 */
export const AGENTIC_LOGIC_LOGGER_LIB_URI =
  "file:///node_modules/@intelligems/agentic-logic-logger/index.d.ts";

export const AGENTIC_LOGIC_DEPENDENCY_LIB_URI =
  "file:///node_modules/@intelligems/agentic-logic-dependencies/index.d.ts";

/**
 * Hidden companion asserting the entry still exports a NAMED `evaluate`
 * matching the contract. This is where Agentic Logic diverges from the
 * shipping provider, which asserts a DEFAULT export.
 *
 * `sanitizeMarkers` collapses every error from this model into the single
 * `diagnosticMessage` below so internal `file:///` URIs never leak, which
 * makes that string the only thing the user ever sees — hence it names both
 * the export and the type.
 */
export const AGENTIC_LOGIC_CONTRACT_CHECK_SOURCE = `
import { evaluate } from "./${AGENTIC_LOGIC_ENTRY_FILENAME.replace(/\.ts$/, "")}";
import type { AgenticLogicEvaluateFn } from "./types";
const __intelligemsAgenticLogicContractCheck: AgenticLogicEvaluateFn = evaluate;
void __intelligemsAgenticLogicContractCheck;
`;

export const AGENTIC_LOGIC_CONTRACT_DIAGNOSTIC_MESSAGE =
  "Your code must export a named async function `evaluate` matching the Agentic Logic contract (AgenticLogicEvaluateFn).";

/**
 * Decodes persisted Agentic Logic source bytes into the editable file set.
 *
 * The two persisted encodings differ: a DRAFT carries the entry module's
 * source inline (`draft.source`), while a published VERSION stores the JSON
 * workspace manifest (`{ entry, files }`) that `uploadVersionArtifacts`
 * writes. Routing both through the shared workspace codec interprets each
 * correctly — a manifest parses into its files, and raw TypeScript falls
 * through the codec's legacy single-file path onto the entry — so the version
 * drawer shows real code instead of a JSON-escaped manifest.
 */
export const decodeAgenticLogicFiles = (raw: string): Record<string, string> =>
  Object.fromEntries(
    orderedWorkspaceFiles(
      parseWorkspace(raw, {
        schema: AgenticLogicRuleWorkspaceSchema,
        entryFilename: AGENTIC_LOGIC_ENTRY_FILENAME,
      })
    ).map((file) => [file.name, file.content])
  );

/**
 * Built per author and org. The org-data accessors (`sdk.getDataset`,
 * `sdk.getSource`) exist only in an admin's projection, and an admin's
 * `AgenticLogicDatasetKey` union is generated from the org's authored dataset
 * keys — without them `sdk.getDataset()` types as `never` and every call
 * red-squiggles. The workspace therefore mounts only once the data surface
 * has SETTLED (see `useAgenticLogicDataSurface`), and falls back to the
 * zero-key projection only when an admin's datasets read actually failed.
 */
export const buildAgenticLogicMonacoConfig = ({
  dataSurface,
}: {
  dataSurface: AgenticLogicDataSurface;
}): WorkspaceMonacoConfig => ({
  loggerLibUri: AGENTIC_LOGIC_LOGGER_LIB_URI,
  loggerDts: AGENTIC_LOGIC_LOGGER_AMBIENT_DTS,
  dependencyLibUri: AGENTIC_LOGIC_DEPENDENCY_LIB_URI,
  typesModule: {
    filename: AGENTIC_LOGIC_TYPES_FILENAME,
    modelUri: AGENTIC_LOGIC_TYPES_MODEL_URI,
    source: buildAgenticLogicTypesModuleSource({
      dataSurface,
      requestFieldNotes: renderAgenticLogicRequestFieldNotes(),
      sdkFieldNotes: renderAgenticLogicSdkFieldNotes(),
    }),
  },
  contractCheck: {
    uri: AGENTIC_LOGIC_CONTRACT_MODEL_URI,
    source: AGENTIC_LOGIC_CONTRACT_CHECK_SOURCE,
    diagnosticMessage: AGENTIC_LOGIC_CONTRACT_DIAGNOSTIC_MESSAGE,
  },
  // The SDK declarations ride as a companion MODEL rather than an extra lib so
  // they sit at a real URI in the model graph and their relative
  // `import("./types")` resolves. See AGENTIC_LOGIC_SDK_LIB_URI. Shown as a
  // locked tab (`readonlyTabs`, not the hidden `extraCompanions`) so rule
  // authors can read what `sdk.*` actually returns.
  readonlyTabs: [
    {
      filename: AGENTIC_LOGIC_SDK_TYPES_FILENAME,
      modelUri: AGENTIC_LOGIC_SDK_LIB_URI,
      source: AGENTIC_LOGIC_SDK_AMBIENT_DTS,
    },
  ],
  // `vitest` and `./test-harness`, for the suites shown as editor files.
  testSuiteCompanions: AGENTIC_LOGIC_TEST_SUITE_COMPANIONS,
});
